import {afterDomBuilt, searchOrFallback} from "./globals.ts";
import {TimerBasedCronScheduler} from "cron-schedule/schedulers/timer-based.js";
import {parseCronExpression} from "cron-schedule";


const cronListParamKey = 'cron'
type CronSchedules = string[]   // array of cron expressions

const classEnableBell = 'tag-enable-bell'   // a marker for toggling, not for rendering
const audioContainerElementName = 'audio-container'

const bellAuthElementName = 'bell-auth'
const textEnableBell = '🔔 启用铃声'
const textDisableBell = '🔇 禁用铃声'

const bellElementName = 'the-bell'
const defaultBellSrc = 'https://jordan-public.oss-cn-beijing.aliyuncs.com/gaokao.daozhi.de/Alarm.mp3'
const getBellSrc = () => searchOrFallback('bell-audio', defaultBellSrc)
const storageAlarmVolumeKey = 'alarmVolume'
const defaultAlarmVolume = 0.3

const volumeControlElementName = 'volumeControl'

const textTestButton = '📢 测试播放'
const textTestButtonDisabled = '音频加载中……'
const textTestButtonEnabled = '点击测试铃声播放'

const classAlarmStatusHidable = 'alarm-status-hidable'
const classFeatureStatus = 'feature-status' // with style

const tempMessageNonReady = '🔇 音频尚未准备好'
const tempMessageSuccess = '🔊 测试播放成功'
const getTempMessageError = (e: any) => `❌ 播放失败: ${e}`


class CronAlarmContext {
    readonly cronSchedules: CronSchedules
    constructor(cronSchedules: CronSchedules) {
        this.cronSchedules = cronSchedules
    }

    bellAudio: HTMLAudioElement | null = null
    readonly testButton = document.createElement('button')
    readonly statusElement = document.createElement('div')

    init(): void {
        this.setupDomElements()
        this.initTestButton()
        this.showBellToggleButton()

        this.initStatusIndicator()
        this.initScheduleCheck()
        this.initVolumeControl()
    }

    async playBell(_force: boolean = false) : Promise<void> {
        if (!this.bellAudio) return // silent ignore as success
        return this.bellAudio.play()
    }

    private isBellReady(): boolean {
        return (!!this.bellAudio) && this.bellAudio.readyState >= HTMLMediaElement.HAVE_ENOUGH_DATA
    }

    // STEP 1 //
    private setupDomElements() {
        const bellAuthButton = document.createElement('button')
        bellAuthButton.id = bellAuthElementName
        bellAuthButton.innerText = textEnableBell
        bellAuthButton.classList.add('settingsButton', classEnableBell)

        const self = this
        bellAuthButton.onclick = function() {
            // toggle
            if (self.bellAudio) {
                self.disableAudio()
            } else {
                self.initAudio()
            }
        }
        document.querySelector('.footer-buttons')?.appendChild(bellAuthButton)
    }

    private initAudio() {
        // Toggle button from [TAP TO ENABLE] to [TAP TO DISABLE]
        const buttonElement = document.getElementById('bell-auth')
        if (buttonElement && buttonElement.classList.contains(classEnableBell)) {
            buttonElement.innerText = textDisableBell
            buttonElement.classList.remove(classEnableBell)
        }

        const maybeBellElement = document.getElementById(bellElementName)
        // implicit null check
        if (maybeBellElement instanceof HTMLAudioElement && !maybeBellElement.error) {
            this.bellAudio = maybeBellElement
        } else {
            if (maybeBellElement) {
                // corrupted element
                maybeBellElement.remove()
            }

            this.bellAudio = document.createElement('audio')
            this.bellAudio.id = bellElementName
            this.bellAudio.src = getBellSrc()
            this.bellAudio.preload = 'auto'
            this.bellAudio.addEventListener('loadeddata', () => console.log('Bell loaded'))
            this.bellAudio.addEventListener('error', (e) => console.error('Failed to load bell', e))

            document.getElementById(audioContainerElementName)?.appendChild(this.bellAudio)
            this.bellAudio.addEventListener('loadeddata', () => this.updateButtonState())
            this.bellAudio.addEventListener('error', () => this.updateButtonState())
        }

        const volumeControlElement = document.getElementById(volumeControlElementName)
        let volumeToSet: number = NaN
        if (volumeControlElement instanceof HTMLInputElement) {
            volumeToSet = parseFloat(volumeControlElement.value)
        }
        this.bellAudio.volume = isNaN(volumeToSet) ? this.readVolumeFromStorage() : volumeToSet
    }

    private disableAudio() {
        this.bellAudio = null   // short-circuit cron schedules (they detect the nullness)
        // Re-initialize the button to recover default state
        const maybeButtonElement = document.getElementById(bellAuthElementName)
        maybeButtonElement?.remove()
        this.setupDomElements()
    }

    private readVolumeFromStorage(): number {
        const storageValue = parseFloat(localStorage.getItem(storageAlarmVolumeKey) ?? '')
        return isNaN(storageValue) ? defaultAlarmVolume : storageValue
    }

    // STEP 2 //
    private initTestButton() {
        this.testButton.innerText = textTestButton
        this.testButton.style.cssText = `
            position: fixed;
            top: 50px;
            right: 10px;
            padding: 6px 12px;
            font-size: 14px;
            background: #e3f2fd;
            border: 1px solid #2196f3;
            color: #1976d2;
            cursor: pointer;
            border-radius: 4px;
            z-index: 1000;
        `
        this.testButton.classList.add(classAlarmStatusHidable)

        this.updateButtonState()
        this.testButton.addEventListener('click', () => this.handleTestPlay())
        document.body.appendChild(this.testButton)
    }

    private updateButtonState() {
        if (!this.bellAudio) return

        this.testButton.disabled = !this.isBellReady()
        this.testButton.title = this.testButton.disabled ? textTestButtonDisabled : textTestButtonEnabled
        this.updateStatusDisplay()
    }

    private handleTestPlay() {
        if (!this.bellAudio) this.initAudio()

        if (!this.bellAudio || !this.isBellReady()) {
            this.showTempMessage(tempMessageNonReady, 'warn')
            return
        }

        this.bellAudio.currentTime = 0
        this.playBell(true)
            .then(() => this.showTempMessage(tempMessageSuccess, 'success'))
            .catch(e => this.showTempMessage(getTempMessageError(e), 'error'))
    }

    private showTempMessage(text: string, type: 'success' | 'warn' | 'error') {
        const backgroundColor = {
            success: '#e8f5e9',
            warn: '#fff3e0',
            error: '#ffebee'
        }[type] || '#f5f5f5'

        const msg = document.createElement('div')
        msg.textContent = text
        msg.style.cssText = `
                position: fixed;
                top: 100px;
                right: 10px;
                padding: 8px 16px;
                border-radius: 4px;
                background: ${backgroundColor};
                box-shadow: 0 2px 4px rgba(0,0,0,0.2);
                z-index: 1000;
                animation: slideIn 0.3s ease-out;
            `;

        document.body.appendChild(msg);
        setTimeout(() => msg.remove(), 3000)
    }

    // STEP 3 //
    private showBellToggleButton() {
        const tryDisplay = (e: HTMLElement | null) => {
            if (e) e.style.display = 'block'
        }
        // at config block
        tryDisplay(document.getElementById('alarm-status-checkbox'))
        tryDisplay(document.getElementById('volumeControlContainer'))
    }

    // STEP 4 //
    private initStatusIndicator() {
        this.statusElement.classList.add(
            classAlarmStatusHidable,
            classFeatureStatus,
        )
        const hideAlarmStatusButton = document.getElementById('hideAlarmStatus')
        if (hideAlarmStatusButton instanceof HTMLInputElement) {    // implicit null check
            hideAlarmStatusButton.addEventListener('change', () => {
                const checked = hideAlarmStatusButton.checked
                for (const element of document.getElementsByClassName(classAlarmStatusHidable)) {
                    if (element instanceof HTMLElement) {
                        element.style.display = checked ? 'block' : 'none'
                    }
                }
            })
        }

        this.updateStatusDisplay()
        document.body.appendChild(this.statusElement)
    }

    private updateStatusDisplay() {
        const [statusClass, statusText] = this.isBellReady() ? ['status-ready', 'READY'] : ['status-idle', 'IDLE']

        this.statusElement.classList.remove('status-ready', 'status-idle')
        this.statusElement.classList.add(statusClass)
        this.statusElement.textContent = `🔔 BELL: ${statusText}`
    }

    // STEP 5 //
    private initScheduleCheck() {
        for (const schedule of this.cronSchedules) {
            const cron = parseCronExpression(schedule);
            TimerBasedCronScheduler.setInterval(cron, async () => {
                if (!this.isBellReady()) return

                console.log('Try play bell at', new Date())
                return this.playBell()
            })
        }
    }

    // STEP 6 //
    private initVolumeControl() {
        const volumeControlElement = document.getElementById(volumeControlElementName)
        const volumeValueElement = document.getElementById('volumeValue')
        if (!(volumeControlElement instanceof HTMLInputElement) || !volumeValueElement) {
            throw new Error('Corrupted DOM tree: volumeControl or volumeValue absent')
        }

        const self = this

        function setVolumeText(volume: number) {
            if (!volumeValueElement) return
            volumeValueElement.textContent = `${Math.round(volume * 100)}%`
        }

        volumeControlElement.addEventListener('input', function() {
            const volume = parseFloat(volumeControlElement.value)
            if (!isNaN(volume)) {
                if (self.bellAudio) self.bellAudio.volume = volume
                setVolumeText(volume)
                localStorage.setItem(storageAlarmVolumeKey, String(volume))
            }
        })

        const savedVolume = this.readVolumeFromStorage()
        volumeControlElement.value = String(savedVolume)
        setVolumeText(savedVolume)
    }
}

async function initializeContext() : Promise<CronAlarmContext | null> {
    const cronListParam = searchOrFallback(cronListParamKey, '')
    if (!cronListParam) return null
    const response = await fetch(cronListParam)
    if (response.ok) {
        try {
            const json = await response.json()
            if (Array.isArray(json) && json.every((v) => typeof v === 'string')) {
                if (json.length === 0) return null
                return new CronAlarmContext(json)
            }
        } catch (e) {
            console.warn('Failed to load custom cron schedules', e)
        }
    } else {
        console.warn('Response for cron schedules failed: HTTP ' + response.status)
    }
    return null
}

let cronAlarmContext: CronAlarmContext | null = await initializeContext()
if (cronAlarmContext) {
    afterDomBuilt(() => cronAlarmContext.init())
}

